/**
 * ConfigurationCelebration Component
 *
 * Displays a confetti celebration animation when a configuration
 * is enabled (default_value set to true).
 */

var React = require('react');
var useState = React.useState;
var useEffect = React.useEffect;
var useMemo = React.useMemo;

var CELEBRATION_DURATION = 2000;
var PARTICLE_COUNT = 50;

// Confetti colors
var COLORS = [
  '#4563c7', // Primary blue
  '#7c3aed', // Purple
  '#059669', // Green
  '#d97706', // Orange
  '#ec4899', // Pink
  '#06b6d4', // Cyan
  '#f59e0b', // Amber
  '#10b981', // Emerald
];

// Emoji options for extra fun
var EMOJIS = ['🎉', '✨', '🎊', '⭐', '🌟', '💫', '🎯', '✅'];

/**
 * Generate random particles
 */
var generateParticles = function () {
  var particles = [];
  for (var i = 0; i < PARTICLE_COUNT; i++) {
    var isEmoji = Math.random() > 0.7;
    particles.push({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 500,
      duration: 1000 + Math.random() * 1000,
      size: isEmoji ? 20 + Math.random() * 10 : 8 + Math.random() * 8,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 720,
      isEmoji: isEmoji,
      emoji: EMOJIS[Math.floor(Math.random() * EMOJIS.length)],
      wobble: Math.random() * 30,
    });
  }
  return particles;
};

/**
 * ConfigurationCelebration component
 * @param {Object} props
 * @param {boolean} props.show - Whether to show the celebration
 * @param {Function} props.onComplete - Called when animation completes
 */
var ConfigurationCelebration = function (props) {
  var show = props.show;
  var onComplete = props.onComplete;

  var visibleState = useState(false);
  var visible = visibleState[0];
  var setVisible = visibleState[1];

  var particles = useMemo(
    function () {
      return generateParticles();
    },
    [show],
  );

  // Dismiss the celebration
  var dismiss = function () {
    setVisible(false);
    if (onComplete) {
      onComplete();
    }
  };

  useEffect(
    function () {
      if (show) {
        setVisible(true);

        // Inline dismiss logic to avoid stale closure issues
        var dismissCelebration = function () {
          setVisible(false);
          if (onComplete) {
            onComplete();
          }
        };

        var timer = setTimeout(dismissCelebration, CELEBRATION_DURATION);

        // Handle Escape key
        var handleKeyDown = function (e) {
          if (e.key === 'Escape') {
            dismissCelebration();
          }
        };

        document.addEventListener('keydown', handleKeyDown);

        return function () {
          clearTimeout(timer);
          document.removeEventListener('keydown', handleKeyDown);
        };
      }
    },
    [show, onComplete],
  );

  if (!visible) {
    return null;
  }

  // Handle click anywhere to dismiss
  var handleClick = function () {
    dismiss();
  };

  return React.createElement(
    'div',
    {
      className: 'remote-config-celebration',
      onClick: handleClick,
      style: { cursor: 'pointer' },
      'aria-hidden': 'true',
    },
    [
      // Confetti particles
      React.createElement(
        'div',
        {
          key: 'confetti',
          className: 'remote-config-celebration__confetti',
        },
        particles.map(function (particle) {
          var style = {
            left: particle.x + '%',
            animationDelay: particle.delay + 'ms',
            animationDuration: particle.duration + 'ms',
          };

          if (particle.isEmoji) {
            return React.createElement(
              'div',
              {
                key: particle.id,
                className: 'remote-config-celebration__emoji',
                style: Object.assign({}, style, {
                  fontSize: particle.size + 'px',
                }),
              },
              particle.emoji,
            );
          }

          return React.createElement('div', {
            key: particle.id,
            className: 'remote-config-celebration__particle',
            style: Object.assign({}, style, {
              width: particle.size + 'px',
              height: particle.size + 'px',
              backgroundColor: particle.color,
              transform: 'rotate(' + particle.rotation + 'deg)',
            }),
          });
        }),
      ),

      // Center burst text
      React.createElement(
        'div',
        {
          key: 'message',
          className: 'remote-config-celebration__message',
        },
        [
          React.createElement(
            'span',
            {
              key: 'emoji',
              className: 'remote-config-celebration__message-emoji',
            },
            '🎉',
          ),
          React.createElement(
            'span',
            {
              key: 'text',
              className: 'remote-config-celebration__message-text',
            },
            'Feature Enabled!',
          ),
          React.createElement(
            'span',
            {
              key: 'emoji2',
              className: 'remote-config-celebration__message-emoji',
            },
            '🎉',
          ),
        ],
      ),
    ],
  );
};

module.exports = ConfigurationCelebration;
